/**
 * Emergency surfaces: the numbers panel, the member ID card, and the
 * full-screen takeover you show to whoever is helping.
 *
 * This is a running club — a member can go down at 6 AM on a road with a
 * stranger holding their phone. These screens are built for that moment:
 * big numbers, one tap to dial, ambulance first.
 *
 * Privacy: an emergency phone number is admin-only. A member sees their own
 * on their own card, and never another member's. SPEC.md §3.10.
 */

import {
  ageFrom,
  bibNo,
  formatDate,
  initials,
  telHref,
} from '../data';
import { PhotoTile } from './Avatar';
import { useGrounds } from '../context/ClubContext';

/** Guardian for a minor, otherwise the stated relationship. */
function iceRelation(m) {
  const age = ageFrom(m.dob);
  if (age !== null && age < 18) return 'Guardian';
  return m.emRel || 'Family contact';
}

function WarningIcon({ stroke, size = 15 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={stroke}
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fff"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
    </svg>
  );
}

/** One tap-to-call row in the SOS grid. `badgeTone` colours the round badge. */
function CallRow({ href, badge, badgeTone = 'bg-ink-800', label, value, note }) {
  return (
    <a
      className="flex items-center gap-2.5 border border-line bg-white px-3 py-3 text-ink-700 no-underline transition-all duration-200 hover:border-ink-700"
      href={href}
    >
      <div
        className={`grid h-[34px] w-[34px] flex-none place-items-center rounded-full font-market text-[13px] font-extrabold text-white ${badgeTone}`}
      >
        {badge}
      </div>
      <div>
        <div className="font-market text-[8.5px] uppercase tracking-[0.12em] text-copy-label">
          {label}
        </div>
        <div className="mt-0.5 font-market text-[17px] font-bold tracking-[0.03em]">{value}</div>
        <div className="text-[11.5px] text-copy-muted">{note}</div>
      </div>
    </a>
  );
}

/**
 * The emergency numbers panel: ambulance, all-emergencies, the club safety
 * contact, and — when viewing your own — your own contact.
 */
export function SosBlock({ member, meta }) {
  const hasPersonal = !!member?.emPhone;
  const minor = member ? (ageFrom(member.dob) ?? 99) < 18 : false;

  return (
    <div className="relative mb-3.5 overflow-hidden border border-flag-100 bg-flag-50 p-4 text-ink-700">
      <div className="mb-3 flex items-center gap-2">
        <div className="grid h-[30px] w-[30px] flex-none place-items-center border border-flag-100 bg-white">
          <WarningIcon stroke="#DC2626" />
        </div>
        <div>
          <div className="font-market text-[17px] font-extrabold tracking-[-0.015em]">
            Emergency numbers
          </div>
          <div className="mt-0.5 font-market text-[9.5px] text-copy-muted">
            Tap to call · save these before your run
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2 min-[520px]:grid-cols-2">
        <CallRow
          href="tel:108"
          badge="108"
          badgeTone="bg-flag-500"
          label="Ambulance"
          value="108"
          note="Free medical emergency"
        />
        <CallRow
          href="tel:112"
          badge="112"
          label="All emergencies"
          value="112"
          note="Police · fire · medical"
        />
        <CallRow
          href={telHref(meta.sosPhone)}
          badge="TR"
          label="Club safety contact"
          value={meta.sosPhone || '—'}
          note={meta.sosName || 'Run lead'}
        />
        {hasPersonal && member && (
          <CallRow
            href={telHref(member.emPhone)}
            badge={initials(member.emName ?? 'EC')}
            label={minor ? 'Guardian' : 'Your emergency contact'}
            value={member.emPhone}
            note={member.emName ?? '—'}
          />
        )}
      </div>

      <div className="mt-3 font-market text-[10px] leading-relaxed text-copy-muted">
        If a runner collapses: call 108 first, then the club safety contact. Every member&rsquo;s
        emergency contact and blood group is on file with the committee.
      </div>
    </div>
  );
}

/** The tiny mono label used all over the ID card. */
const ID_K = 'font-market text-[8px] uppercase tracking-[0.13em] text-copy-label';

/**
 * The printable member ID card. Carries blood group, age, bib and the
 * in-case-of-emergency contact — the things a paramedic needs.
 */
export function IdCard({ member }) {
  const grounds = useGrounds();
  const age = ageFrom(member.dob);
  const hasContact = !!member.emPhone;

  return (
    <div className="mb-3.5">
      <div className="overflow-hidden border border-line bg-white">
        <div className="flex items-center justify-between gap-2.5 border-b border-line bg-white px-4 py-3">
          <div className="font-market text-[15px] font-extrabold leading-none tracking-[-0.015em]">
            Tirupur<span className="text-gold-800">·</span>Runners
            <small className="mt-1 block font-market text-[7.5px] font-normal tracking-[0.16em] text-copy-label">
              MEMBER ID CARD
            </small>
          </div>
          <div className="text-right font-market text-[8.5px] tracking-[0.1em] text-copy-label">
            VALID
            <br />
            {member.tier === 'life'
              ? 'LIFE'
              : member.duesPaidTill
                ? formatDate(member.duesPaidTill).toUpperCase()
                : '—'}
          </div>
        </div>

        <div className="flex items-start gap-4 p-4">
          <PhotoTile
            member={member}
            className="h-[88px] w-[74px] flex-none border border-line bg-line-chip"
            initialsClassName="text-[30px] tracking-[0.02em] text-copy-label"
          />
          <div className="min-w-0 flex-1">
            <div className={ID_K}>Runner</div>
            <div className="mt-0.5 font-market text-[25px] font-extrabold leading-[1.05] tracking-[-0.03em]">
              {member.name}
            </div>
            <div className="mt-3 flex flex-wrap gap-4">
              <div>
                <div className={ID_K}>Blood group</div>
                <div className="mt-0.5 font-market text-lg font-semibold text-flag-600">
                  {member.blood || '—'}
                </div>
              </div>
              <div>
                <div className={ID_K}>Age</div>
                <div className="mt-0.5 font-market text-[15px] font-semibold">{age ?? '—'}</div>
              </div>
              <div>
                <div className={ID_K}>Ground</div>
                <div className="mt-0.5 font-market text-xs font-semibold">
                  {grounds[member.ground]?.split(' ')[0] ?? '—'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-y border-line bg-ground px-4 py-2.5">
          <div>
            <div className={ID_K}>Bib number</div>
            <div className="font-market text-[30px] font-extrabold leading-none">
              {bibNo(member.bib)}
            </div>
          </div>
          <div className="text-right">
            <div className={ID_K}>Member since</div>
            <div className="mt-1 font-market text-[13px] font-semibold">
              {member.joined ? formatDate(member.joined) : '—'}
            </div>
          </div>
        </div>

        <div className="border-t-2 border-flag-600 bg-flag-50 px-4 py-3.5">
          <div className="mb-2 flex items-center gap-1.5">
            <WarningIcon stroke="#B91C1C" size={14} />
            <div className="font-market text-[13px] font-extrabold uppercase tracking-[0.1em] text-flag-600">
              In case of emergency — call
            </div>
          </div>

          {hasContact ? (
            <div className="flex items-center gap-2.5">
              <div className="min-w-0 flex-1">
                <div className="font-market text-[17px] font-bold tracking-[-0.015em]">
                  {member.emName || 'Contact'}
                </div>
                <div className="mt-px font-market text-[9.5px] text-flag-600">
                  {iceRelation(member)}
                </div>
                <div className="mt-1 font-market text-[17px] font-semibold tabular-nums">
                  {member.emPhone}
                </div>
              </div>
              <a
                className="flex flex-none items-center gap-1.5 bg-flag-500 px-3.5 py-3 font-market text-sm font-bold uppercase tracking-[0.05em] text-white no-underline transition-all duration-200 hover:brightness-110"
                href={telHref(member.emPhone)}
              >
                <PhoneIcon />
                Call
              </a>
            </div>
          ) : (
            <>
              <div className="font-market text-[17px] font-bold tracking-[-0.015em] text-flag-600">
                No emergency contact on file
              </div>
              <div className="mt-px font-market text-[9.5px] text-flag-600">
                Tell the committee so this card is complete
              </div>
            </>
          )}
        </div>

        {member.medical && (
          <div className="border-t border-gold-100 bg-gold-50 px-4 py-2.5">
            <div className="font-market text-[8px] uppercase tracking-[0.12em] text-gold-800">
              Medical notes
            </div>
            <div className="mt-1 text-[13px] leading-normal text-gold-900">{member.medical}</div>
          </div>
        )}

        <div className="px-4 py-2.5 text-center font-market text-[8.5px] leading-relaxed text-copy-label">
          If this runner needs help — call 108 for an ambulance, then the contact above.
          <br />
          Tirupur Runners · est. 2013
        </div>
      </div>
    </div>
  );
}

/* The takeover renders inside UIContext's #sosScreen — a deliberately loud
   solid red screen. These are its shared text styles. */
const SS_K = 'font-market text-[9px] uppercase tracking-[0.16em] text-white/70';
const SS_BTN =
  'mt-3.5 flex items-center justify-center gap-2 px-4 py-4 font-market text-[19px] font-extrabold tracking-[-0.025em] no-underline';

/**
 * The full-screen red takeover. Everything a bystander needs, nothing else:
 * who to call, blood group, medical notes, and 108.
 */
export function SosScreen({ member, onClose }) {
  const age = ageFrom(member.dob);
  const firstName = (member.emName || 'contact').split(' ')[0];

  return (
    <>
      <div className="pt-2 text-center">
        {member.photo && (
          <img
            className="mx-auto mb-3.5 block h-[86px] w-[86px] rounded-full border-[3px] border-white/50 object-cover"
            src={member.photo}
            alt=""
          />
        )}
        <div className="inline-block rounded-full border-2 border-white/50 px-4 py-1.5 font-market text-[10px] uppercase tracking-[0.22em]">
          Emergency information
        </div>
        <div className="mt-4 font-market text-[clamp(30px,8vw,44px)] font-extrabold uppercase leading-none tracking-[0.02em]">
          {member.name}
        </div>
        <div className="mt-2.5 text-sm leading-normal text-white/85">
          Bib {bibNo(member.bib)} · Tirupur Runners
          <br />
          Show this screen to whoever is helping.
        </div>
      </div>

      <div className="mt-3.5 grid grid-cols-2 gap-2.5">
        <div className="bg-black/15 p-3">
          <div className="font-market text-[8.5px] uppercase tracking-[0.13em] text-white/65">
            Blood group
          </div>
          <div className="mt-1 font-market text-[22px] font-extrabold">{member.blood || '—'}</div>
        </div>
        <div className="bg-black/15 p-3">
          <div className="font-market text-[8.5px] uppercase tracking-[0.13em] text-white/65">
            Age
          </div>
          <div className="mt-1 font-market text-[22px] font-extrabold">{age ?? '—'}</div>
        </div>
      </div>

      {member.medical && (
        <div className="mt-3 border border-white/30 bg-white/15 p-4">
          <div className={SS_K}>Medical notes</div>
          <div className="mt-1.5 text-[15px] leading-normal">{member.medical}</div>
        </div>
      )}

      <div className="mt-5 border border-white/30 bg-white/15 p-4">
        <div className={SS_K}>Call this person first</div>
        <div className="mt-1 font-market text-[27px] font-extrabold leading-tight tracking-[-0.03em]">
          {member.emName || 'No contact on file'}
        </div>
        <div className={`${SS_K} mt-1.5`}>{iceRelation(member)}</div>
        <div className="mt-1.5 font-market text-2xl font-semibold tabular-nums">
          {member.emPhone || '—'}
        </div>
        {member.emPhone && (
          <a className={`${SS_BTN} bg-white text-[#B3122B]`} href={telHref(member.emPhone)}>
            Call {firstName}
          </a>
        )}
        <a className={`${SS_BTN} border-[1.5px] border-white/50 bg-white/15 text-white`} href="tel:108">
          Call 108 · Ambulance
        </a>
      </div>

      <div className="mt-auto pt-5 text-center">
        <button
          type="button"
          className="border-[1.5px] border-white/40 px-5 py-3 font-market text-[11px] uppercase tracking-[0.1em] text-white"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </>
  );
}
